import { DAY_MAP, timeBoundHHMM } from "@/lib/schedule"
import { normalizeWeekHours, tenantClock } from "@/lib/scheduling/normalize-hours"
import { DayOfWeek, ServiceHours } from "@/types"

const DAY_NAMES_PT: Record<DayOfWeek, string> = {
  SUNDAY: "Domingo",
  MONDAY: "Segunda-feira",
  TUESDAY: "Terça-feira",
  WEDNESDAY: "Quarta-feira",
  THURSDAY: "Quinta-feira",
  FRIDAY: "Sexta-feira",
  SATURDAY: "Sábado",
}

export interface NextOpening {
  /** Relative day label: today, tomorrow, or a weekday name. */
  dayLabel: string
  /** Opening time formatted as "HH:mm". */
  time: string
  /**
   * Days from today until that opening (0 = today). Used to pick the earliest
   * opening when comparing several channels (e.g. DELIVERY vs TAKEOUT).
   */
  dayOffset: number
}

/**
 * Given week hours, returns the next opening time as structured parts.
 * - Opens later today / tomorrow / further weekday, or null if no schedule.
 */
export function getNextOpening(
  weekHours: ServiceHours["weekHours"],
  now: Date = new Date(),
  timeZone?: string | null,
): NextOpening | null {
  if (!weekHours || weekHours.length === 0) return null

  const clock = tenantClock(now, timeZone)
  const currentDayIndex = DAY_MAP.indexOf(clock.day)
  const windows = normalizeWeekHours(weekHours, timeZone)

  for (let offset = 0; offset < 7; offset++) {
    const dayName = DAY_MAP[(currentDayIndex + offset) % 7]

    const starts = windows
      .filter((window) => window.day === dayName)
      .map((window) => timeBoundHHMM(window.startTime))
      .sort((a, b) => a.localeCompare(b))

    for (const time of starts) {
      if (offset === 0 && time <= clock.time) continue

      if (offset === 0) return { dayLabel: "hoje", time, dayOffset: 0 }
      if (offset === 1) return { dayLabel: "amanhã", time, dayOffset: 1 }
      return { dayLabel: DAY_NAMES_PT[dayName], time, dayOffset: offset }
    }
  }

  return null
}

/** Sort key: smaller = sooner. */
export function nextOpeningSortKey(opening: NextOpening): number {
  const [hh = 0, mm = 0] = opening.time.split(":").map((n) => Number(n))
  return opening.dayOffset * 24 * 60 + hh * 60 + mm
}

/**
 * Earliest next opening among several week-hour schedules (e.g. delivery + takeout
 * sharing the storefront menu).
 */
export function getEarliestNextOpening(
  schedules: Array<ServiceHours["weekHours"] | null | undefined>,
  now: Date = new Date(),
  timeZone?: string | null,
): NextOpening | null {
  let best: NextOpening | null = null
  for (const weekHours of schedules) {
    const next = getNextOpening(weekHours ?? [], now, timeZone)
    if (!next) continue
    if (!best || nextOpeningSortKey(next) < nextOpeningSortKey(best)) {
      best = next
    }
  }
  return best
}

/**
 * Given week hours, returns a human-friendly next-opening message, or null
 * if there is no schedule at all.
 */
export function getNextOpeningMessage(
  weekHours: ServiceHours["weekHours"],
  now: Date = new Date(),
  timeZone?: string | null,
): string | null {
  const next = getNextOpening(weekHours, now, timeZone)
  if (!next) return null
  return `Abre ${next.dayLabel} às ${next.time}`
}
